<template lang="pug">
web-app-layout(:showLogo="true" :noPadding="true")
  .insights-page
    //- Header
    .page-header
      button(@click="router.back()" type="button").back-button
        img(src="@/assets/icons/chevron-right.svg" alt="Back").back-icon
        span Ticket Insights

    //- Loading
    .loading-state(v-if="loading && !insights")
      .spinner
      p.loading-text Loading insights...

    //- Content
    .insights-content(v-if="insights")
      //- Top Metrics Cards
      .metrics-grid
        .metric-card
          .metric-icon 🎟️
          .metric-value {{ insights.totalAdmissions }}
          .metric-label Total Admissions

        .metric-card
          .metric-icon ✅
          .metric-value {{ insights.checkedInCount }}
          .metric-label Checked In

        .metric-card
          .metric-icon 👥
          .metric-value {{ insights.distinctBuyers }}
          .metric-label Distinct Buyers

        .metric-card
          .metric-icon 💰
          .metric-value ${{ formatCurrency(insights.grossRevenue) }}
          .metric-label Gross Revenue

        .metric-card(v-if="insights.totalRaised > 0")
          .metric-icon 💚
          .metric-value ${{ formatCurrency(insights.totalRaised) }}
          .metric-label Total Raised

      //- Activity Cards Row
      .activity-cards
        .activity-card(@click="goToQuestions")
          .activity-info
            .activity-label Help Questions
            .activity-count {{ insights.questionsTotal }} total
            .activity-badge(v-if="insights.questionsUnanswered > 0") {{ insights.questionsUnanswered }} unanswered
          .activity-arrow
            img(src="@/assets/icons/chevron-right.svg" alt="View")

        .activity-card(@click="goToAnnouncements")
          .activity-info
            .activity-label Announcements
            .activity-count {{ insights.announcementsCount }} sent
          .activity-arrow
            img(src="@/assets/icons/chevron-right.svg" alt="View")

        .activity-card(@click="goToCheckIn")
          .activity-info
            .activity-label RSVP & Check-In
            .activity-count {{ insights.totalAdmissions }} attendees
          .activity-arrow
            img(src="@/assets/icons/chevron-right.svg" alt="View")

      //- Tier Breakdown
      .tier-section
        h3.tier-heading Ticket Tiers Breakdown

        .tier-list
          .tier-card(v-for="tier in sortedTiers" :key="tier.variantId")
            .tier-header
              .tier-title-row
                .tier-name {{ tier.title }}
                .tier-type-badge(:class="{ 'is-addon': tier.isAddOn }") {{ tier.isAddOn ? 'Add-on' : 'Main Tier' }}
              .tier-stats
                .tier-stat
                  span.stat-label Sold:
                  span.stat-value {{ tier.sold }}{{ tier.capacity ? ` / ${tier.capacity}` : '' }}
                .tier-stat
                  span.stat-label Checked In:
                  span.stat-value {{ tier.checkedIn }} / {{ tier.sold }}
                .tier-stat
                  span.stat-label Revenue:
                  span.stat-value ${{ formatCurrency(tier.revenue) }}

            //- Capacity Progress
            .progress-section(v-if="tier.capacity")
              .progress-label Capacity Utilization
              .progress-bar-container
                .progress-bar
                  .progress-fill(:style="{ width: getCapacityPercent(tier) + '%' }")
                .progress-text {{ getCapacityPercent(tier) }}%

            //- Check-in Progress
            .progress-section(v-if="tier.sold > 0")
              .progress-label Check-in Rate
              .progress-bar-container
                .progress-bar
                  .progress-fill.checkin(:style="{ width: getCheckInPercent(tier) + '%' }")
                .progress-text {{ getCheckInPercent(tier) }}%

    //- Empty State
    .empty-state(v-else-if="!loading")
      .empty-icon 📊
      h3.empty-title Failed to load insights
      p.empty-subtitle Please try again later
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import { makeRequest } from '@/graphs/index';
import { getTicketInsights } from '@/graphs/gql';

interface TicketTierInsight {
  variantId: string;
  title: string;
  isAddOn: boolean;
  capacity: number | null;
  sold: number;
  checkedIn: number;
  revenue: number;
}

interface TicketInsights {
  totalAdmissions: number;
  checkedInCount: number;
  distinctBuyers: number;
  grossRevenue: number;
  totalRaised: number;
  questionsTotal: number;
  questionsUnanswered: number;
  announcementsCount: number;
  tiers: TicketTierInsight[];
}

export default defineComponent({
  name: 'HostTicketInsights',
  components: { WebAppLayout },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const stickerId = String(route.params.stickerId);
    const loading = ref(false);
    const insights = ref<TicketInsights | null>(null);

    const sortedTiers = computed(() => {
      if (!insights.value) return [];
      
      // Sort: main tiers first, then add-ons
      return [...insights.value.tiers].sort((a, b) => {
        if (a.isAddOn === b.isAddOn) return 0;
        return a.isAddOn ? 1 : -1;
      });
    });

    async function fetchInsights() {
      loading.value = true;
      try {
        const response = await makeRequest(
          'query',
          getTicketInsights,
          { stickerId },
          null,
        );
        if (response?.errors) throw new Error(response.errors[0]?.message);
        insights.value = response?.data?.getTicketInsights || null;
      } catch (err: any) {
        console.error('Failed to load insights:', err);
        ElMessage.error('Failed to load insights');
      } finally {
        loading.value = false;
      }
    }

    function formatCurrency(amount: number): string {
      return amount.toFixed(2);
    }

    function getCapacityPercent(tier: TicketTierInsight): number {
      if (!tier.capacity) return 0;
      return Math.round((tier.sold / tier.capacity) * 100);
    }

    function getCheckInPercent(tier: TicketTierInsight): number {
      if (tier.sold === 0) return 0;
      return Math.round((tier.checkedIn / tier.sold) * 100);
    }

    function goToQuestions() {
      router.push(`/web/event/${stickerId}/questions`);
    }

    function goToAnnouncements() {
      router.push(`/web/event/${stickerId}/announcements`);
    }

    function goToCheckIn() {
      router.push(`/web/event/${stickerId}/checkin`);
    }

    onMounted(fetchInsights);

    return {
      router,
      loading,
      insights,
      sortedTiers,
      formatCurrency,
      getCapacityPercent,
      getCheckInPercent,
      goToQuestions,
      goToAnnouncements,
      goToCheckIn,
    };
  },
});
</script>

<style lang="scss" scoped>
.insights-page {
  min-height: 100vh;
  background: #f5f5f7;
  padding-bottom: 4rem;
}

.page-header {
  display: flex;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 10;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
}

.back-icon {
  width: 1.125rem;
  height: 1.125rem;
  transform: rotate(180deg);
  opacity: 0.6;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 1.5rem;
  gap: 1rem;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 5px solid #e0e0e0;
  border-top-color: #0d9488;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0;
}

.insights-content {
  padding: 1.5rem;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(5, 1fr);
  }
}

.metric-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem;
  text-align: center;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
}

.metric-icon {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.metric-value {
  font-size: 1.75rem;
  font-weight: 900;
  color: #1a1a1a;
  margin-bottom: 0.25rem;
}

.metric-label {
  font-size: 0.8rem;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.activity-cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.activity-card {
  background: #fff;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #0d9488;
    box-shadow: 0 4px 12px rgba(13, 148, 136, 0.15);
  }
}

.activity-info {
  flex: 1;
}

.activity-label {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.25rem;
}

.activity-count {
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.activity-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: #ef4444;
  color: white;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
}

.activity-arrow {
  width: 1.25rem;
  height: 1.25rem;
  opacity: 0.4;
  transition: opacity 0.2s;

  .activity-card:hover & {
    opacity: 1;
  }
}

.tier-section {
  margin-top: 2rem;
}

.tier-heading {
  font-size: 1.25rem;
  font-weight: 900;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}

.tier-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.tier-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem;
}

.tier-header {
  margin-bottom: 1rem;
}

.tier-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  gap: 1rem;
}

.tier-name {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1a1a1a;
  flex: 1;
}

.tier-type-badge {
  padding: 0.25rem 0.625rem;
  background: #0d9488;
  color: white;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;

  &.is-addon {
    background: #6366f1;
  }
}

.tier-stats {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.tier-stat {
  display: flex;
  gap: 0.375rem;
  font-size: 0.875rem;
}

.stat-label {
  color: #6b7280;
  font-weight: 600;
}

.stat-value {
  color: #1a1a1a;
  font-weight: 700;
}

.progress-section {
  margin-top: 1rem;
}

.progress-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.5rem;
}

.progress-bar-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.progress-bar {
  flex: 1;
  height: 1.25rem;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
  position: relative;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #f59e0b 0%, #f97316 100%);
  border-radius: 999px;
  transition: width 0.4s ease;

  &.checkin {
    background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%);
  }
}

.progress-text {
  font-size: 0.8rem;
  font-weight: 700;
  color: #6b7280;
  min-width: 3rem;
  text-align: right;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 1.5rem;
  text-align: center;
}

.empty-icon {
  font-size: 3.5rem;
  margin-bottom: 1rem;
  opacity: 0.4;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem;
}

.empty-subtitle {
  font-size: 0.9rem;
  color: #6b7280;
  margin: 0;
}
</style>
